// Comprehensive field definitions for Custom Reports - Frontend
// Updated: October 14, 2025
// Now includes ALL fields (100) with IDs and reference numbers to match backend

export interface FieldDefinition {
  name: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'array';
  source: string;
  path: string;
  category: string;
  description: string;
  accessLevel: string[];
  isCalculated?: boolean;
  formula?: string;
  dependsOn?: string[];
}

export const COMPREHENSIVE_FIELD_DEFINITIONS: FieldDefinition[] = [
  // ===== USER FIELDS (18 fields) =====
  {
    name: 'user_id',
    label: 'User ID',
    type: 'string',
    source: 'users',
    path: '_id',
    category: 'User Information',
    description: 'Unique MongoDB ObjectId for the user',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_ref_no',
    label: 'User Reference Number',
    type: 'string',
    source: 'users',
    path: 'refNo',
    category: 'User Information',
    description: 'Unique reference number for the user',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_title',
    label: 'User Title',
    type: 'string',
    source: 'users',
    path: 'title',
    category: 'User Information',
    description: 'User title (Mr, Mrs, Dr, etc.)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_first_name',
    label: 'User First Name',
    type: 'string',
    source: 'users',
    path: 'firstName',
    category: 'User Information',
    description: 'First name of the user',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_last_name',
    label: 'User Last Name',
    type: 'string',
    source: 'users',
    path: 'lastName',
    category: 'User Information',
    description: 'Last name of the user',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_email',
    label: 'User Email',
    type: 'string',
    source: 'users',
    path: 'email',
    category: 'User Information',
    description: 'Email address of the user',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_mobile_number',
    label: 'User Mobile Number',
    type: 'string',
    source: 'users',
    path: 'mobileNumber',
    category: 'User Information',
    description: 'Mobile phone number of the user',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_gender',
    label: 'User Gender',
    type: 'string',
    source: 'users',
    path: 'gender',
    category: 'User Information',
    description: 'Gender of the user (MALE, FEMALE, NON_BINARY)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_status',
    label: 'User Status',
    type: 'string',
    source: 'users',
    path: 'status',
    category: 'User Information',
    description: 'Current status of the user account (INVITED, PENDING, APPROVED, DENIED, DEACTIVATED, LOCKED)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_is_active',
    label: 'User Is Active',
    type: 'boolean',
    source: 'users',
    path: 'isActive',
    category: 'User Information',
    description: 'Whether the user account is active',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_is_archived',
    label: 'User Is Archived',
    type: 'boolean',
    source: 'users',
    path: 'isArchived',
    category: 'User Information',
    description: 'Whether the user account is archived',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_company_name',
    label: 'User Company Name',
    type: 'string',
    source: 'users',
    path: 'companyName',
    category: 'User Information',
    description: 'Company name associated with the user',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_citizenship',
    label: 'User Citizenship',
    type: 'string',
    source: 'users',
    path: 'citizenship',
    category: 'User Information',
    description: 'Citizenship of the user',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_national_insurance_no',
    label: 'User National Insurance Number',
    type: 'string',
    source: 'users',
    path: 'nationalInsuranceNo',
    category: 'User Information',
    description: 'National insurance number of the user',
    accessLevel: ['CSM', 'SM', 'SA', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_tax_number',
    label: 'User Tax Number',
    type: 'string',
    source: 'users',
    path: 'taxNumber',
    category: 'User Information',
    description: 'Tax number of the user',
    accessLevel: ['CSM', 'SM', 'SA', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_trade_licence',
    label: 'User Trade Licence',
    type: 'string',
    source: 'users',
    path: 'tradeLicence',
    category: 'User Information',
    description: 'User trade licence status (YES/NO)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_is_first_login',
    label: 'User Is First Login',
    type: 'boolean',
    source: 'users',
    path: 'isFirstLogin',
    category: 'User Information',
    description: 'Whether this is the user\'s first login',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'user_created_date',
    label: 'User Created Date',
    type: 'date',
    source: 'users',
    path: 'createdAt',
    category: 'User Information',
    description: 'Date when user account was created',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },

  // ===== SUBSCRIBER FIELDS (13 fields) =====
  {
    name: 'subscriber_id',
    label: 'Subscriber ID',
    type: 'string',
    source: 'subscribers',
    path: '_id',
    category: 'Subscriber Information',
    description: 'Unique MongoDB ObjectId for the subscriber',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_ref_no',
    label: 'Subscriber Reference Number',
    type: 'string',
    source: 'subscribers',
    path: 'refNo',
    category: 'Subscriber Information',
    description: 'Unique reference number for the subscriber',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_company_name',
    label: 'Subscriber Company Name',
    type: 'string',
    source: 'subscribers',
    path: 'companyName',
    category: 'Subscriber Information',
    description: 'Company name of the subscriber',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_first_name',
    label: 'Subscriber First Name',
    type: 'string',
    source: 'subscribers',
    path: 'firstName',
    category: 'Subscriber Information',
    description: 'First name of the subscriber',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_last_name',
    label: 'Subscriber Last Name',
    type: 'string',
    source: 'subscribers',
    path: 'lastName',
    category: 'Subscriber Information',
    description: 'Last name of the subscriber',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_email',
    label: 'Subscriber Email',
    type: 'string',
    source: 'subscribers',
    path: 'email',
    category: 'Subscriber Information',
    description: 'Email address of the subscriber',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_status',
    label: 'Subscriber Status',
    type: 'string',
    source: 'subscribers',
    path: 'status',
    category: 'Subscriber Information',
    description: 'Current status of the subscriber (Active, Inactive)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_is_paid',
    label: 'Subscriber Is Paid',
    type: 'boolean',
    source: 'subscribers',
    path: 'isPaid',
    category: 'Subscriber Information',
    description: 'Whether the subscriber has paid',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_plan_name',
    label: 'Subscriber Plan Name',
    type: 'string',
    source: 'subscribers',
    path: 'payment.planName',
    category: 'Subscriber Information',
    description: 'Name of the subscriber\'s payment plan',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_plan_id',
    label: 'Subscriber Plan ID',
    type: 'string',
    source: 'subscribers',
    path: 'payment.planId',
    category: 'Subscriber Information',
    description: 'ID of the subscriber\'s payment plan',
    accessLevel: ['CSM', 'SM', 'SA', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_payment_status',
    label: 'Subscriber Payment Status',
    type: 'string',
    source: 'subscribers',
    path: 'payment.status',
    category: 'Subscriber Information',
    description: 'Current payment status of the subscriber',
    accessLevel: ['CSM', 'SM', 'SA', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_created_date',
    label: 'Subscriber Created Date',
    type: 'date',
    source: 'subscribers',
    path: 'createdAt',
    category: 'Subscriber Information',
    description: 'Date when subscriber account was created',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'subscriber_updated_date',
    label: 'Subscriber Updated Date',
    type: 'date',
    source: 'subscribers',
    path: 'updatedAt',
    category: 'Subscriber Information',
    description: 'Date when subscriber account was last updated',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },

  // ===== CUSTOMER FIELDS (18 fields) =====
  {
    name: 'customer_id',
    label: 'Customer ID',
    type: 'string',
    source: 'customers',
    path: '_id',
    category: 'Customer Information',
    description: 'Unique MongoDB ObjectId for the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_ref_no',
    label: 'Customer Reference Number',
    type: 'string',
    source: 'customers',
    path: 'refNo',
    category: 'Customer Information',
    description: 'Unique reference number for the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_business_name',
    label: 'Customer Business Name',
    type: 'string',
    source: 'customers',
    path: 'businessName',
    category: 'Customer Information',
    description: 'Business name of the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_business_id',
    label: 'Customer Business ID',
    type: 'string',
    source: 'customers',
    path: 'businessId',
    category: 'Customer Information',
    description: 'Business ID or tax/registration number of the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_zip_code',
    label: 'Customer Zip Code',
    type: 'string',
    source: 'customers',
    path: 'zipCode',
    category: 'Customer Information',
    description: 'Zip code of the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_city',
    label: 'Customer City',
    type: 'string',
    source: 'customers',
    path: 'address.city',
    category: 'Customer Information',
    description: 'City where the customer is located',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_address_street',
    label: 'Customer Street Address',
    type: 'string',
    source: 'customers',
    path: 'address.streetAddress',
    category: 'Customer Information',
    description: 'Street address of the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_address_formatted',
    label: 'Customer Full Address',
    type: 'string',
    source: 'customers',
    path: 'address.formattedAddress',
    category: 'Customer Information',
    description: 'Full formatted address of the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_industry',
    label: 'Customer Industry',
    type: 'string',
    source: 'customers',
    path: 'industry',
    category: 'Customer Information',
    description: 'Industry sector of the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_number_of_employees',
    label: 'Customer Number of Employees',
    type: 'string',
    source: 'customers',
    path: 'numberOfEmployees',
    category: 'Customer Information',
    description: 'Number of employees at the customer company',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_interest_level',
    label: 'Customer Interest Level',
    type: 'string',
    source: 'customers',
    path: 'interestLevel',
    category: 'Customer Information',
    description: 'Interest level of the customer',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_accepted_promotional_gifts',
    label: 'Customer Accepted Promotional Gifts',
    type: 'boolean',
    source: 'customers',
    path: 'acceptedPromotionalGifts',
    category: 'Customer Information',
    description: 'Whether customer accepted promotional gifts',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_google_place_id',
    label: 'Customer Google Place ID',
    type: 'string',
    source: 'customers',
    path: 'googlePlaceId',
    category: 'Customer Information',
    description: 'Google Place ID for the customer location',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_coordinates_lat',
    label: 'Customer Latitude',
    type: 'number',
    source: 'customers',
    path: 'coordinates.coordinates.1',
    category: 'Customer Information',
    description: 'Latitude coordinate of customer location',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_coordinates_lng',
    label: 'Customer Longitude',
    type: 'number',
    source: 'customers',
    path: 'coordinates.coordinates.0',
    category: 'Customer Information',
    description: 'Longitude coordinate of customer location',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_decision_maker_name',
    label: 'Primary Decision Maker Name',
    type: 'string',
    source: 'customers',
    path: 'decisionMaker.0.firstName',
    category: 'Customer Information',
    description: 'First name of primary decision maker',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_decision_maker_email',
    label: 'Primary Decision Maker Email',
    type: 'string',
    source: 'customers',
    path: 'decisionMaker.0.email',
    category: 'Customer Information',
    description: 'Email of primary decision maker',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'customer_created_date',
    label: 'Customer Created Date',
    type: 'date',
    source: 'customers',
    path: 'createdAt',
    category: 'Customer Information',
    description: 'Date when customer was created',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },

  // ===== PRODUCT FIELDS (13 fields) =====
  {
    name: 'product_id',
    label: 'Product ID',
    type: 'string',
    source: 'products',
    path: '_id',
    category: 'Product Information',
    description: 'Unique MongoDB ObjectId for the product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_ref_no',
    label: 'Product Reference Number',
    type: 'string',
    source: 'products',
    path: 'refNo',
    category: 'Product Information',
    description: 'Unique reference number for the product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_name',
    label: 'Product Name',
    type: 'string',
    source: 'products',
    path: 'name',
    category: 'Product Information',
    description: 'Name of the product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_code',
    label: 'Product Code',
    type: 'string',
    source: 'products',
    path: 'code',
    category: 'Product Information',
    description: 'Unique code of the product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_description',
    label: 'Product Description',
    type: 'string',
    source: 'products',
    path: 'description',
    category: 'Product Information',
    description: 'Description of the product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_min_price',
    label: 'Product Minimum Price',
    type: 'number',
    source: 'products',
    path: 'minPrice',
    category: 'Product Information',
    description: 'Minimum price of the product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_max_price',
    label: 'Product Maximum Price',
    type: 'number',
    source: 'products',
    path: 'maxPrice',
    category: 'Product Information',
    description: 'Maximum price of the product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_currency',
    label: 'Product Currency',
    type: 'string',
    source: 'products',
    path: 'currency',
    category: 'Product Information',
    description: 'Currency of the product pricing (EUR, USD, etc.)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_reservations',
    label: 'Product Reservations Count',
    type: 'number',
    source: 'products',
    path: 'reservations',
    category: 'Product Information',
    description: 'Number of reservations for this product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_is_active',
    label: 'Product Is Active',
    type: 'boolean',
    source: 'products',
    path: 'isActive',
    category: 'Product Information',
    description: 'Whether the product is active',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_is_archived',
    label: 'Product Is Archived',
    type: 'boolean',
    source: 'products',
    path: 'isArchived',
    category: 'Product Information',
    description: 'Whether the product is archived',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_subscriber_id',
    label: 'Product Subscriber ID',
    type: 'string',
    source: 'products',
    path: 'subscriberId',
    category: 'Product Information',
    description: 'ID of the subscriber who owns this product',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'product_created_date',
    label: 'Product Created Date',
    type: 'date',
    source: 'products',
    path: 'createdAt',
    category: 'Product Information',
    description: 'Date when product was created',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },

  // ===== RESERVATION FIELDS (12 fields) =====
  {
    name: 'reservation_id',
    label: 'Reservation ID',
    type: 'string',
    source: 'reservations',
    path: '_id',
    category: 'Reservation Information',
    description: 'Unique MongoDB ObjectId for the reservation',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_ref_no',
    label: 'Reservation Reference Number',
    type: 'string',
    source: 'reservations',
    path: 'refNo',
    category: 'Reservation Information',
    description: 'Unique reference number for the reservation',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_status',
    label: 'Reservation Status',
    type: 'string',
    source: 'reservations',
    path: 'status',
    category: 'Reservation Information',
    description: 'Current status of the reservation (Pending, Interested, Not Interested, Converted, Expired)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_user_id',
    label: 'Reservation FSR User ID',
    type: 'string',
    source: 'reservations',
    path: 'userId',
    category: 'Reservation Information',
    description: 'ID of the FSR user who owns this reservation',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_customer_id',
    label: 'Reservation Customer ID',
    type: 'string',
    source: 'reservations',
    path: 'customerId',
    category: 'Reservation Information',
    description: 'ID of the customer for this reservation',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_subscriber_id',
    label: 'Reservation Subscriber ID',
    type: 'string',
    source: 'reservations',
    path: 'subscriberId',
    category: 'Reservation Information',
    description: 'ID of the subscriber for this reservation',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_product_ids',
    label: 'Reservation Product IDs',
    type: 'array',
    source: 'reservations',
    path: 'products',
    category: 'Reservation Information',
    description: 'Array of product IDs for this reservation',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_expiry_date',
    label: 'Reservation Expiry Date',
    type: 'date',
    source: 'reservations',
    path: 'expiryDate',
    category: 'Reservation Information',
    description: 'Date when reservation expires',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_created_date',
    label: 'Reservation Created Date',
    type: 'date',
    source: 'reservations',
    path: 'createdAt',
    category: 'Reservation Information',
    description: 'Date when reservation was created',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_updated_date',
    label: 'Reservation Updated Date',
    type: 'date',
    source: 'reservations',
    path: 'updatedAt',
    category: 'Reservation Information',
    description: 'Date when reservation was last updated',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_reminder_sent_at',
    label: 'Reservation Reminder Sent At',
    type: 'date',
    source: 'reservations',
    path: 'reminderSentAt',
    category: 'Reservation Information',
    description: 'Date when reminder was sent for reservation',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'reservation_expired_on',
    label: 'Reservation Expired On',
    type: 'date',
    source: 'reservations',
    path: 'expiredOn',
    category: 'Reservation Information',
    description: 'Date when reservation actually expired',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },

  // ===== CONTRACT FIELDS (15 fields) =====
  {
    name: 'contract_id',
    label: 'Contract ID',
    type: 'string',
    source: 'contracts',
    path: '_id',
    category: 'Contract Information',
    description: 'Unique MongoDB ObjectId for the contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_ref_no',
    label: 'Contract Reference Number',
    type: 'string',
    source: 'contracts',
    path: 'refNo',
    category: 'Contract Information',
    description: 'Unique reference number for the contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_customer_id',
    label: 'Contract Customer ID',
    type: 'string',
    source: 'contracts',
    path: 'customerId',
    category: 'Contract Information',
    description: 'ID of the customer for this contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_reservation_id',
    label: 'Contract Reservation ID',
    type: 'string',
    source: 'contracts',
    path: 'reservationId',
    category: 'Contract Information',
    description: 'ID of the reservation that led to this contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_subscriber_id',
    label: 'Contract Subscriber ID',
    type: 'string',
    source: 'contracts',
    path: 'subscriberId',
    category: 'Contract Information',
    description: 'ID of the subscriber for this contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_approver_id',
    label: 'Contract Approver User ID',
    type: 'string',
    source: 'contracts',
    path: 'approverId',
    category: 'Contract Information',
    description: 'ID of the user who approved this contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_fsr_id',
    label: 'Contract FSR User ID',
    type: 'string',
    source: 'contracts',
    path: 'fsrId',
    category: 'Contract Information',
    description: 'ID of the FSR user who created this contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_products',
    label: 'Contract Products',
    type: 'array',
    source: 'contracts',
    path: 'products',
    category: 'Contract Information',
    description: 'Array of product names in this contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_value',
    label: 'Contract Value',
    type: 'number',
    source: 'contracts',
    path: 'contractValue',
    category: 'Contract Information',
    description: 'Monetary value of the contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_duration',
    label: 'Contract Duration',
    type: 'string',
    source: 'contracts',
    path: 'duration',
    category: 'Contract Information',
    description: 'Duration of the contract (e.g., "12 months", "24 months")',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_start_date',
    label: 'Contract Start Date',
    type: 'date',
    source: 'contracts',
    path: 'startDate',
    category: 'Contract Information',
    description: 'Start date of the contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_end_date',
    label: 'Contract End Date',
    type: 'date',
    source: 'contracts',
    path: 'endDate',
    category: 'Contract Information',
    description: 'End date of the contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_status',
    label: 'Contract Status',
    type: 'string',
    source: 'contracts',
    path: 'status',
    category: 'Contract Information',
    description: 'Current status of the contract (Draft, Active, Expired, Rejected, Cancelled)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_description',
    label: 'Contract Description',
    type: 'string',
    source: 'contracts',
    path: 'description',
    category: 'Contract Information',
    description: 'Description of the contract',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'contract_created_date',
    label: 'Contract Created Date',
    type: 'date',
    source: 'contracts',
    path: 'createdAt',
    category: 'Contract Information',
    description: 'Date when contract was created',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },

  // ===== INTERACTION FIELDS (11 fields) =====
  {
    name: 'interaction_id',
    label: 'Interaction ID',
    type: 'string',
    source: 'interactions',
    path: '_id',
    category: 'Interaction Information',
    description: 'Unique MongoDB ObjectId for the interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_ref_no',
    label: 'Interaction Reference Number',
    type: 'string',
    source: 'interactions',
    path: 'refNo',
    category: 'Interaction Information',
    description: 'Unique reference number for the interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_type',
    label: 'Interaction Type',
    type: 'string',
    source: 'interactions',
    path: 'interactionType',
    category: 'Interaction Information',
    description: 'Type of interaction (call, visit, mail, email, meeting)',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_date',
    label: 'Interaction Date',
    type: 'date',
    source: 'interactions',
    path: 'interactionDate',
    category: 'Interaction Information',
    description: 'Date of the interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_time',
    label: 'Interaction Time',
    type: 'string',
    source: 'interactions',
    path: 'interactionTime',
    category: 'Interaction Information',
    description: 'Time of the interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_status',
    label: 'Interaction Status',
    type: 'string',
    source: 'interactions',
    path: 'interactionStatus',
    category: 'Interaction Information',
    description: 'Status of the interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_notes',
    label: 'Interaction Notes',
    type: 'string',
    source: 'interactions',
    path: 'interactionNotes',
    category: 'Interaction Information',
    description: 'Notes from the interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_reservation_id',
    label: 'Interaction Reservation ID',
    type: 'string',
    source: 'interactions',
    path: 'reservationId',
    category: 'Interaction Information',
    description: 'ID of the reservation related to this interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_subscriber_id',
    label: 'Interaction Subscriber ID',
    type: 'string',
    source: 'interactions',
    path: 'subscriberId',
    category: 'Interaction Information',
    description: 'ID of the subscriber for this interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_reminder_sent_at',
    label: 'Interaction Reminder Sent At',
    type: 'date',
    source: 'interactions',
    path: 'reminderSentAt',
    category: 'Interaction Information',
    description: 'Date when reminder was sent for this interaction',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  },
  {
    name: 'interaction_created_date',
    label: 'Interaction Created Date',
    type: 'date',
    source: 'interactions',
    path: 'createdAt',
    category: 'Interaction Information',
    description: 'Date when interaction was recorded',
    accessLevel: ['CSM', 'SM', 'SA', 'A', 'FSR', 'SUPER_DEV_ADMIN']
  }
];

// Field categories for better organization
export const FIELD_CATEGORIES = [
  'User Information',
  'Subscriber Information',
  'Customer Information',
  'Product Information',
  'Reservation Information',
  'Contract Information',
  'Interaction Information'
];

// Calculated field templates
export interface CalculatedFieldTemplate {
  name: string;
  label: string;
  formula: string;
  type: 'mathematical' | 'aggregation' | 'conditional' | 'string_operation' | 'date_operation';
  returnType: 'number' | 'string' | 'date' | 'boolean';
  description: string;
  category: string;
  dependsOn: string[];
}

export const CALCULATED_FIELD_TEMPLATES: CalculatedFieldTemplate[] = [
  // Aggregation templates
  {
    name: 'active_subscriptions',
    label: 'Number of Active Subscriptions',
    formula: 'COUNT(CASE WHEN subscriber_status = "Active" THEN 1 END)',
    type: 'aggregation',
    returnType: 'number',
    description: 'Count of active subscriber accounts',
    category: 'Subscriber Metrics',
    dependsOn: ['subscriber_status']
  },
  {
    name: 'total_users',
    label: 'Total Number of Users',
    formula: 'COUNT(DISTINCT user_ref_no)',
    type: 'aggregation',
    returnType: 'number',
    description: 'Total unique users in the system',
    category: 'User Metrics',
    dependsOn: ['user_ref_no']
  },
  {
    name: 'total_reservations',
    label: 'Total Number of Reservations',
    formula: 'COUNT(reservation_id)',
    type: 'aggregation',
    returnType: 'number',
    description: 'Total count of all reservations',
    category: 'Reservation Metrics',
    dependsOn: ['reservation_id']
  },
  {
    name: 'reservations_per_user',
    label: 'Reservations Per User',
    formula: 'COUNT(reservation_id) / COUNT(DISTINCT user_ref_no)',
    type: 'mathematical',
    returnType: 'number',
    description: 'Average number of reservations per user',
    category: 'Performance Metrics',
    dependsOn: ['reservation_id', 'user_ref_no']
  },
  {
    name: 'active_reservations',
    label: 'Number of Active Reservations',
    formula: 'COUNT(CASE WHEN reservation_status IN ("Pending", "Interested") THEN 1 END)',
    type: 'aggregation',
    returnType: 'number',
    description: 'Count of active/pending reservations',
    category: 'Reservation Metrics',
    dependsOn: ['reservation_status']
  },
  {
    name: 'total_contract_value',
    label: 'Total Value of Contracts',
    formula: 'SUM(contract_value)',
    type: 'aggregation',
    returnType: 'number',
    description: 'Sum of all contract values',
    category: 'Contract Metrics',
    dependsOn: ['contract_value']
  },
  {
    name: 'average_contract_value',
    label: 'Average Contract Value',
    formula: 'AVG(contract_value)',
    type: 'aggregation',
    returnType: 'number',
    description: 'Average value across all contracts',
    category: 'Contract Metrics',
    dependsOn: ['contract_value']
  },
  {
    name: 'contracts_created',
    label: 'Number of Contracts Created',
    formula: 'COUNT(contract_id)',
    type: 'aggregation',
    returnType: 'number',
    description: 'Total number of contracts created',
    category: 'Contract Metrics',
    dependsOn: ['contract_id']
  },
  {
    name: 'win_loss_ratio',
    label: 'Win/Loss Ratio',
    formula: '(COUNT(CASE WHEN contract_status = "Active" THEN 1 END) / COUNT(CASE WHEN reservation_status = "Expired" THEN 1 END)) * 100',
    type: 'mathematical',
    returnType: 'number',
    description: 'Percentage ratio of won vs lost opportunities',
    category: 'Performance Metrics',
    dependsOn: ['contract_status', 'reservation_status']
  },
  {
    name: 'conversion_rate',
    label: 'Reservation to Contract Conversion Rate',
    formula: '(COUNT(CASE WHEN reservation_status = "Converted" THEN 1 END) / COUNT(reservation_id)) * 100',
    type: 'mathematical',
    returnType: 'number',
    description: 'Percentage of reservations converted to contracts',
    category: 'Performance Metrics',
    dependsOn: ['reservation_status', 'reservation_id']
  },
  {
    name: 'expiring_soon',
    label: 'Reservations Expiring in Next 7 Days',
    formula: 'COUNT(CASE WHEN DATEDIFF(reservation_expiry_date, NOW()) <= 7 AND DATEDIFF(reservation_expiry_date, NOW()) >= 0 THEN 1 END)',
    type: 'conditional',
    returnType: 'number',
    description: 'Count of reservations expiring within the next week',
    category: 'Reservation Metrics',
    dependsOn: ['reservation_expiry_date']
  },

  // Rolling time periods
  {
    name: 'reservations_last_30_days',
    label: 'Reservations in Last 30 Days',
    formula: 'COUNT(CASE WHEN DATEDIFF(NOW(), reservation_created_date) <= 30 THEN 1 END)',
    type: 'conditional',
    returnType: 'number',
    description: 'Reservations created in the past 30 days',
    category: 'Time-based Metrics',
    dependsOn: ['reservation_created_date']
  },
  {
    name: 'contracts_last_month',
    label: 'Contracts in Last Month',
    formula: 'COUNT(CASE WHEN DATEDIFF(NOW(), contract_created_date) <= 30 THEN 1 END)',
    type: 'conditional',
    returnType: 'number',
    description: 'Contracts created in the last month',
    category: 'Time-based Metrics',
    dependsOn: ['contract_created_date']
  },
  {
    name: 'contract_value_last_month',
    label: 'Contract Value in Last Month',
    formula: 'SUM(CASE WHEN DATEDIFF(NOW(), contract_created_date) <= 30 THEN contract_value ELSE 0 END)',
    type: 'conditional',
    returnType: 'number',
    description: 'Total value of contracts created in last month',
    category: 'Time-based Metrics',
    dependsOn: ['contract_created_date', 'contract_value']
  },
  {
    name: 'interactions_per_week',
    label: 'Average Interactions Per Week',
    formula: 'COUNT(interaction_id) / (DATEDIFF(MAX(interaction_date), MIN(interaction_date)) / 7)',
    type: 'mathematical',
    returnType: 'number',
    description: 'Average number of interactions per week over the period',
    category: 'Activity Metrics',
    dependsOn: ['interaction_id', 'interaction_date']
  },
  {
    name: 'interactions_per_win',
    label: 'Interactions Per Win',
    formula: 'COUNT(interaction_id) / COUNT(CASE WHEN contract_status = "Active" THEN 1 END)',
    type: 'mathematical',
    returnType: 'number',
    description: 'Average number of interactions needed to close a contract',
    category: 'Performance Metrics',
    dependsOn: ['interaction_id', 'contract_status']
  }
];

// Field summary statistics
export const FIELD_STATS = {
  totalFields: COMPREHENSIVE_FIELD_DEFINITIONS.length,
  byCategory: {
    'User Information': COMPREHENSIVE_FIELD_DEFINITIONS.filter(f => f.category === 'User Information').length,
    'Subscriber Information': COMPREHENSIVE_FIELD_DEFINITIONS.filter(f => f.category === 'Subscriber Information').length,
    'Customer Information': COMPREHENSIVE_FIELD_DEFINITIONS.filter(f => f.category === 'Customer Information').length,
    'Product Information': COMPREHENSIVE_FIELD_DEFINITIONS.filter(f => f.category === 'Product Information').length,
    'Reservation Information': COMPREHENSIVE_FIELD_DEFINITIONS.filter(f => f.category === 'Reservation Information').length,
    'Contract Information': COMPREHENSIVE_FIELD_DEFINITIONS.filter(f => f.category === 'Contract Information').length,
    'Interaction Information': COMPREHENSIVE_FIELD_DEFINITIONS.filter(f => f.category === 'Interaction Information').length
  }
};

console.log('📊 Frontend Custom Report Fields Loaded:');
console.log(`   Total Fields: ${FIELD_STATS.totalFields}`);
console.log(`   By Category:`, FIELD_STATS.byCategory);

